import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';

/**
 * Hook for the Feedback Inbox page.
 *
 * Phase 22 — the in-admin "submit report" form was removed (the storefront
 * widget is now the canonical intake channel). This hook only manages the
 * inbox list + status/locale/source filters + accept/reject decisions.
 *
 * `decide` now accepts an optional `severity` arg which drives the leaderboard
 * point award when the decision is 'applied'.
 */
export default function useTranslationFeedback() {
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inbox filters
  const [statusFilter, setStatusFilter] = useState('open'); // open | applied | rejected | ''
  const [localeFilter, setLocaleFilter] = useState('');     // '' | 'de' | 'es' | 'pl' | ...
  const [sourceFilter, setSourceFilter] = useState('');     // '' | 'storefront_widget' | 'admin'

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (localeFilter) params.locale = localeFilter;
      if (sourceFilter) params.source = sourceFilter;
      const res = await axios.get('/api/transly/translation-feedback/', { params });
      setEntries(res.data.feedback || []);
      setCounts(res.data.counts || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, localeFilter, sourceFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const decide = useCallback(async (feedbackId, decision, decisionNotes = null, severity = null) => {
    // Optimistic update — flip status (+ severity on accept); rollback on error.
    const prev = entries;
    setEntries((curr) =>
      curr.map((e) =>
        e.id === feedbackId
          ? {
              ...e,
              status: decision,
              severity: decision === 'applied' && severity ? severity : e.severity,
              decision_notes: decisionNotes,
              decided_at: new Date().toISOString(),
            }
          : e,
      ),
    );
    try {
      const body = { decision, decision_notes: decisionNotes };
      if (decision === 'applied' && severity) body.severity = severity;
      await axios.patch(`/api/transly/translation-feedback/${feedbackId}/decision/`, body);
      await fetchData();
    } catch (err) {
      setEntries(prev);
      throw err;
    }
  }, [entries, fetchData]);

  return {
    entries,
    counts,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    localeFilter,
    setLocaleFilter,
    sourceFilter,
    setSourceFilter,
    decide,
    fetchData,
  };
}
